import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { useHandleWithdrawRequest } from "@/hooks/useHandleWithdrawRequest";
import { useHydrozoaHeadInfo } from "@/hooks/useHydrozoaScriptAddress";
import { useBrandName } from "@/hooks/useBrandName";
import {
  formatAssetLabel,
  formatAssetValue,
  assetIdKey,
  assetIdToApi,
  getAssetDecimals,
} from "@/lib/marketUtils";
import { DepositStepHeader } from "@/components/DepositOverlay/DepositStepHeader";
import { AssetAmountField } from "@/components/AssetAmountField";
import { generateWithdrawId } from "@/context/GummiwormContext/hooks/useWithdrawTxs";
import type { AssetId, AssetValue } from "@module-bindings/types";

type WithdrawStep = "amount" | "review" | "processing";

interface WithdrawOverlayProps {
  open: boolean;
  onClose: () => void;
  onCloseStart?: () => void;
  /** If set, open directly to the processing view for this withdrawal */
  viewWithdrawId?: string | null;
}

/** Minimum withdrawal: 2 ADA in lovelace */
const MIN_WITHDRAW_LOVELACE = 2_000_000n;
/** Minimum remaining balance if not fully withdrawing: 2 ADA in lovelace */
const MIN_REMAINING_LOVELACE = 2_000_000n;

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      className="sr-surface-pill flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full text-sr-neutral-300 transition-colors hover:text-white"
      onClick={onClick}
      aria-label="Go back"
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  );
}

interface WithdrawEntry {
  asset: AssetId;
  key: string;
  label: string;
  decimals: number;
  available: bigint;
  amount: string;
}

type WithdrawStage = "requesting" | "approved" | "settling" | "settled" | "failed";

/**
 * Fetch the latest transaction hash for an address from Blockfrost.
 * Returns null if no transactions found or on error.
 */
async function fetchLatestTxHash(address: string): Promise<string | null> {
  try {
    const network = window.__APP_CONFIG.env === "mainnet" ? "cardano-mainnet" : "cardano-preview";
    const res = await fetch(
      `https://${network}.blockfrost.io/api/v0/addresses/${address}/transactions?count=1&order=desc`,
      { headers: { project_id: window.__APP_CONFIG.blockfrostProjectId } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) return data[0].tx_hash;
    return null;
  } catch {
    return null;
  }
}

export const WithdrawOverlay: FC<WithdrawOverlayProps> = ({
  open,
  onClose,
  onCloseStart,
  viewWithdrawId,
}) => {
  const [step, setStep] = useState<WithdrawStep>("amount");
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<WithdrawStage>("requesting");
  const [settlementTxHash, setSettlementTxHash] = useState<string | null>(null);
  const [entries, setEntries] = useState<WithdrawEntry[]>([]);
  const [activeWithdrawId, setActiveWithdrawId] = useState<string | null>(null);
  const baselineTxHashRef = useRef<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  const { ledgerView, myAccountId, withdrawTxs, addWithdrawTx, updateWithdrawTx } = useGummiwormContext();
  const { mainAddress } = useWalletObserver();
  const brandName = useBrandName();
  const myAccount = ledgerView.account;
  const headInfo = useHydrozoaHeadInfo();
  const headOnline = !!headInfo;
  const { mutateAsync: withdraw } = useHandleWithdrawRequest();

  // Build available balances from account
  const availableAssets = useMemo<WithdrawEntry[]>(() => {
    if (!myAccount) return [];
    return myAccount.uncommittedValue.assets
      .filter((av: AssetValue) => av.value > 0n)
      .map((av: AssetValue) => ({
        asset: av.asset,
        key: assetIdKey(av.asset),
        label: formatAssetLabel(av.asset),
        decimals: getAssetDecimals(av.asset),
        available: av.value,
        amount: "",
      }));
  }, [myAccount]);

  // Cleanup polling on unmount / close
  useEffect(() => {
    return () => {
      if (pollingRef.current != null) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current != null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const activeWithdrawIdRef = useRef<string | null>(null);
  // Keep ref in sync so the polling callback sees the current ID
  useEffect(() => { activeWithdrawIdRef.current = activeWithdrawId; }, [activeWithdrawId]);

  const startSettlementPolling = useCallback(() => {
    // Poll the head address for new txs — settlement txs go *out* from the head
    const headAddress = headInfo?.headAddress;
    if (!headAddress) return;
    stopPolling();

    pollingRef.current = window.setInterval(async () => {
      const latestHash = await fetchLatestTxHash(headAddress);
      if (latestHash && latestHash !== baselineTxHashRef.current) {
        setSettlementTxHash(latestHash);
        setStage("settled");
        stopPolling();
        toast.success("Withdrawal settled! Funds are in your wallet.");
        if (activeWithdrawIdRef.current) {
          updateWithdrawTx(activeWithdrawIdRef.current, {
            status: "settled",
            settlementTxHash: latestHash,
          });
        }
      }
    }, 10_000);
  }, [headInfo?.headAddress, stopPolling, updateWithdrawTx]);

  // Reset state when overlay opens (but not when balances change mid-flow)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      if (viewWithdrawId) {
        // Restore state from an existing withdrawal
        const existing = withdrawTxs.get(viewWithdrawId);
        if (existing) {
          setStep("processing");
          setStage(existing.status);
          setError(existing.errorMessage ?? null);
          setSettlementTxHash(existing.settlementTxHash ?? null);
          setActiveWithdrawId(viewWithdrawId);
          // Resume polling if still settling
          if (existing.status === "settling" || existing.status === "approved") {
            const headAddress = headInfo?.headAddress;
            if (headAddress) {
              fetchLatestTxHash(headAddress).then((hash) => {
                baselineTxHashRef.current = hash;
                startSettlementPolling();
              });
            }
          }
        } else {
          // Withdraw ID not found — fall back to amount step
          setStep("amount");
          setError(null);
          setStage("requesting");
          setSettlementTxHash(null);
          setActiveWithdrawId(null);
          setEntries(availableAssets.map((a) => ({ ...a, amount: "" })));
        }
      } else {
        setStep("amount");
        setError(null);
        setStage("requesting");
        setSettlementTxHash(null);
        setActiveWithdrawId(null);
        baselineTxHashRef.current = null;
        setEntries(availableAssets.map((a) => ({ ...a, amount: "" })));
      }
    }
    prevOpenRef.current = open;
  }, [open, viewWithdrawId, availableAssets, withdrawTxs, headInfo?.headAddress, startSettlementPolling]);

  const handleClose = useCallback(() => {
    setClosing(true);
    onCloseStart?.();
    stopPolling();
    setTimeout(() => {
      setClosing(false);
      setStep("amount");
      setError(null);
      setStage("requesting");
      setSettlementTxHash(null);
      baselineTxHashRef.current = null;
      onClose();
    }, 200);
  }, [onClose, onCloseStart, stopPolling]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, handleClose]);

  const updateAmount = useCallback(
    (key: string, amount: string) => {
      setEntries((prev) =>
        prev.map((e) => (e.key === key ? { ...e, amount } : e)),
      );
    },
    [],
  );

  const setMax = useCallback(
    (key: string) => {
      setEntries((prev) =>
        prev.map((e) => {
          if (e.key !== key) return e;
          const divisor = 10n ** BigInt(e.decimals);
          const whole = e.available / divisor;
          const frac = e.available % divisor;
          const fracStr = frac
            .toString()
            .padStart(e.decimals, "0")
            .replace(/0+$/, "");
          const amount = fracStr ? `${whole}.${fracStr}` : whole.toString();
          return { ...e, amount };
        }),
      );
    },
    [],
  );

  // Parse amounts to raw values
  const parsedEntries = useMemo(() => {
    return entries.map((e) => {
      const trimmed = e.amount.trim();
      if (!trimmed || !/^\d*\.?\d*$/.test(trimmed)) {
        return { ...e, rawAmount: 0n };
      }
      const parts = trimmed.split(".");
      const wholePart = parts[0] || "0";
      const fracPart = (parts[1] || "").padEnd(e.decimals, "0").slice(0, e.decimals);
      const raw = BigInt(wholePart) * 10n ** BigInt(e.decimals) + BigInt(fracPart);
      return { ...e, rawAmount: raw };
    });
  }, [entries]);

  // Find the ADA entry specifically for validation
  const adaEntry = useMemo(
    () => parsedEntries.find((e) => e.asset.tag === "Ada"),
    [parsedEntries],
  );

  // Validation
  const validation = useMemo(() => {
    const hasAnyAmount = parsedEntries.some((e) => e.rawAmount > 0n);
    if (!hasAnyAmount) return { valid: false, error: null };

    // Check no entry exceeds balance
    for (const e of parsedEntries) {
      if (e.rawAmount > e.available) {
        return { valid: false, error: `${e.label} amount exceeds available balance.` };
      }
    }

    // Must withdraw at least 2 ADA
    if (!adaEntry || adaEntry.rawAmount < MIN_WITHDRAW_LOVELACE) {
      return {
        valid: false,
        error: "You must withdraw at least 2 ADA.",
      };
    }

    // If not withdrawing all ADA, must leave at least 2 ADA
    const adaRemaining = adaEntry.available - adaEntry.rawAmount;
    if (adaRemaining > 0n && adaRemaining < MIN_REMAINING_LOVELACE) {
      return {
        valid: false,
        error: "You must leave at least 2 ADA in your account, or withdraw everything.",
      };
    }

    return { valid: true, error: null };
  }, [parsedEntries, adaEntry]);

  const activeEntries = useMemo(
    () => parsedEntries.filter((e) => e.rawAmount > 0n),
    [parsedEntries],
  );

  const amountLabel = useMemo(() => {
    // When viewing an existing withdrawal, use stored label
    if (viewWithdrawId) {
      const existing = withdrawTxs.get(viewWithdrawId);
      if (existing?.amountLabel) return existing.amountLabel;
    }
    const primary = activeEntries[0];
    if (!primary) return "";
    const label = `${formatAssetValue(primary.asset, primary.rawAmount)} ${primary.label}`;
    return activeEntries.length > 1
      ? `${label} + ${activeEntries.length - 1} other${activeEntries.length > 2 ? "s" : ""}`
      : label;
  }, [activeEntries, viewWithdrawId, withdrawTxs]);

  const handleConfirmWithdraw = useCallback(async () => {
    setError(null);
    setStep("processing");
    setStage("requesting");

    const withdrawId = generateWithdrawId();
    setActiveWithdrawId(withdrawId);

    // Save to local storage immediately
    addWithdrawTx(withdrawId, {
      status: "requesting",
      amountLabel,
      submittedAt: Math.floor(Date.now() / 1000),
    });

    // Snapshot the latest tx hash on the head address so we can detect a new settlement
    const headAddress = headInfo?.headAddress;
    if (headAddress) {
      baselineTxHashRef.current = await fetchLatestTxHash(headAddress);
    }

    try {
      const assets = activeEntries.map((e) => ({
        asset: assetIdToApi(e.asset),
        value: Number(e.rawAmount),
      }));

      await withdraw({
        value: { assets },
      });

      // L2 accepted — move to approved, then start watching for L1 settlement
      setStage("approved");
      updateWithdrawTx(withdrawId, { status: "approved" });
      setTimeout(() => {
        setStage("settling");
        updateWithdrawTx(withdrawId, { status: "settling" });
      }, 1500);
      startSettlementPolling();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setStage("failed");
      updateWithdrawTx(withdrawId, { status: "failed", errorMessage: msg });
    }
  }, [activeEntries, amountLabel, withdraw, headInfo?.headAddress, startSettlementPolling, addWithdrawTx, updateWithdrawTx]);

  if (!open) return null;

  const useBottomSheetLayout = step === "processing";

  return (
    <div
      className={`sr-overlay ${useBottomSheetLayout ? "justify-end px-0 pt-4 sm:justify-center sm:p-4" : ""}`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        aria-hidden="true"
        className={`sr-overlay-backdrop ${closing ? "sr-overlay-backdrop-exit" : "sr-overlay-backdrop-enter"}`}
      />
      <button
        onClick={handleClose}
        className={`fixed right-4 top-4 z-20 rounded-full border border-white/[0.03] bg-sr-surface p-2 text-sr-neutral-500 transition-colors hover:text-white sm:right-6 sm:top-6 ${useBottomSheetLayout ? "hidden sm:block" : ""}`}
        aria-label="Close withdraw flow"
      >
        <CloseIcon />
      </button>
      <div
        className={`relative z-10 flex w-full max-w-[420px] flex-col gap-8 items-center ${closing ? "sr-overlay-content-exit" : "sr-overlay-content-enter"} ${
          useBottomSheetLayout
            ? "mt-auto gap-0 rounded-t-[32px] bg-sr-surface pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:mt-0 sm:gap-8 sm:rounded-none sm:bg-transparent sm:pt-0 sm:pb-0"
            : ""
        }`}
      >
        <div
          className={`w-full flex flex-col gap-8 items-center ${useBottomSheetLayout ? "gap-0 px-0 sm:gap-8 sm:px-0" : ""}`}
        >
          {step === "amount" && (
            <AmountStep
              entries={entries}
              onAmountChange={updateAmount}
              onMax={setMax}
              validationError={validation.error}
              canReview={validation.valid}
              onConfirm={() => setStep("review")}
            />
          )}

          {step === "review" && (
            <ReviewStep
              entries={activeEntries}
              error={error}
              headOnline={headOnline}
              onConfirm={handleConfirmWithdraw}
              onBack={() => setStep("amount")}
            />
          )}

          {step === "processing" && (
            <ProcessingStep
              stage={stage}
              error={error}
              amountLabel={amountLabel}
              settlementTxHash={settlementTxHash}
              brandName={brandName}
              onClose={handleClose}
              onRetry={() => {
                stopPolling();
                setStep("review");
                setError(null);
                setStage("requesting");
              }}
            />
          )}

          {useBottomSheetLayout && (
            <div className="w-full px-4 pt-0 sm:hidden">
              <button
                onClick={handleClose}
                className="w-full rounded-full border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-sm font-medium text-sr-neutral-300 transition-colors hover:bg-white/[0.05] hover:text-white"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Sub-components ---

const AmountStep: FC<{
  entries: WithdrawEntry[];
  onAmountChange: (key: string, amount: string) => void;
  onMax: (key: string) => void;
  validationError: string | null;
  canReview: boolean;
  onConfirm: () => void;
}> = ({ entries, onAmountChange, onMax, validationError, canReview, onConfirm }) => {
  return (
    <>
      <DepositStepHeader
        title="Withdraw funds"
        description="Choose how much to withdraw from your account."
      />

      <div className="w-full">
        <div className="flex max-h-[34rem] w-full flex-col gap-2 overflow-y-auto pr-1">
          {entries.map((entry) => (
            <AssetAmountField
              key={entry.key}
              amount={entry.amount}
              label={`Withdraw ${entry.label}`}
              onAmountChange={(value) => onAmountChange(entry.key, value)}
              token={{ assetId: entry.key, name: entry.label }}
              rightTopContent={
                <button
                  onClick={() => onMax(entry.key)}
                  className="sr-focus-ring rounded-full bg-sr-pink-glow px-2 py-0.5 text-[10px] font-medium transition-colors"
                  style={{ color: "#F888E4" }}
                >
                  MAX
                </button>
              }
              amountClassName="text-[28px] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              footerText={
                <>
                  <span>Available:</span>
                  <br />
                  <span className="text-sr-bubblegum">
                    {formatAssetValue(entry.asset, entry.available)} {entry.label}
                  </span>
                </>
              }
            />
          ))}
        </div>
      </div>

      {validationError && (
        <div className="w-full rounded-xl bg-sr-salmon/10 p-3">
          <p className="text-xs text-sr-salmon">{validationError}</p>
        </div>
      )}

      <button
        className="sr-btn-primary w-full"
        disabled={!canReview}
        onClick={onConfirm}
      >
        Review Withdrawal
      </button>
    </>
  );
};

const ReviewStep: FC<{
  entries: { asset: AssetId; label: string; rawAmount: bigint }[];
  error: string | null;
  headOnline: boolean;
  onConfirm: () => void;
  onBack: () => void;
}> = ({ entries, error, headOnline, onConfirm, onBack }) => {
  return (
    <div className="flex flex-col gap-12 items-center w-full">
      <DepositStepHeader
        title="Confirm your withdrawal"
        description="Your funds will be sent back to your L1 wallet."
      />

      <div className="flex flex-col gap-3 w-full">
        <div className="sr-card w-full p-3 sm:p-4">
          <div className="flex flex-col gap-2">
            {entries.map((entry, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-sr-pink-muted">
                  {i === 0 ? "Withdraw" : ""}
                </span>
                <span className="text-sm text-white">
                  {formatAssetValue(entry.asset, entry.rawAmount)} {entry.label}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">Network</span>
              <span className="text-sm text-sr-pink-muted">
                Gummiworm L2 → Cardano L1
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-sr-pink-muted">Est. time</span>
              <span className="text-sm text-white">a few min</span>
            </div>
          </div>
        </div>

        <div className="flex w-full items-center gap-3">
          <BackButton onClick={onBack} />
          <button
            className="sr-btn-primary flex-1"
            disabled={!headOnline}
            onClick={onConfirm}
          >
            {headOnline ? "Confirm and withdraw" : "Unavailable"}
          </button>
        </div>

        <div className="sr-highlight-box w-full">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-sr-pink-light">
              What happens next?
            </p>
            <ol className="text-xs text-sr-pink-bright list-decimal ml-4 flex flex-col gap-1">
              <li>Your withdrawal is submitted to the L2</li>
              <li>The L2 settles your funds back to Cardano L1</li>
              <li>Funds arrive in your wallet</li>
            </ol>
          </div>
        </div>
      </div>

      {!headOnline && (
        <div className="w-full rounded-xl bg-sr-tangerine/10 p-3">
          <p className="text-xs text-sr-tangerine">
            Sugar Rush is offline right now. Please try again later.
          </p>
        </div>
      )}

      {error && (
        <div className="w-full rounded-xl bg-sr-salmon/10 p-3">
          <p className="text-xs text-sr-salmon">{error}</p>
        </div>
      )}
    </div>
  );
};

// --- Processing step with deposit-like stepper ---

const WITHDRAW_STAGES = [
  { label: "Approved", activeLabel: "Requesting" },
  { label: "Submitted", activeLabel: "Processing" },
  { label: "Settled", activeLabel: "Waiting for Settlement" },
];

function getActiveStageIndex(stage: WithdrawStage): number {
  switch (stage) {
    case "requesting":
      return 0;
    case "approved":
      return 1;
    case "settling":
      return 2;
    case "settled":
      return 3; // all done
    case "failed":
      return 0;
  }
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="#170B10"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type BadgeVariant = "done" | "active" | "waiting" | "failed";

function StatusBadge({
  variant,
  label,
  className,
}: {
  variant: BadgeVariant;
  label?: string;
  className?: string;
}) {
  const config = {
    done: { label: "Done", className: "bg-sr-mint/15 text-sr-mint" },
    active: { label: "In progress", className: "bg-sr-tangerine/15 text-sr-tangerine" },
    waiting: { label: "Waiting", className: "bg-sr-neutral-700/30 text-sr-neutral-700" },
    failed: { label: "Failed", className: "bg-sr-salmon/15 text-sr-salmon" },
  };
  const { label: defaultLabel, className: baseClassName } = config[variant];
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${baseClassName} ${className ?? ""}`}>
      {label ?? defaultLabel}
    </span>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

const ProcessingStep: FC<{
  stage: WithdrawStage;
  error: string | null;
  amountLabel: string;
  settlementTxHash: string | null;
  brandName: string;
  onClose: () => void;
  onRetry: () => void;
}> = ({ stage, error, amountLabel, settlementTxHash, brandName, onClose, onRetry }) => {
  const isFailed = stage === "failed";
  const isComplete = stage === "settled";
  const activeIdx = getActiveStageIndex(stage);

  const explorerHref = settlementTxHash
    ? `https://${window.__APP_CONFIG.env === "mainnet" ? "" : "preview."}cexplorer.io/tx/${settlementTxHash}`
    : null;

  const statusDetail = isFailed
    ? { variant: "failed" as const, label: "Failed" }
    : isComplete
      ? { variant: "done" as const, label: "Settled" }
      : activeIdx === 0
        ? { variant: "active" as const, label: "Requesting" }
        : activeIdx === 1
          ? { variant: "active" as const, label: "Processing" }
          : { variant: "active" as const, label: "Waiting for Settlement" };

  return (
    <div className="sr-card w-full rounded-[32px] px-4 pt-3 pb-6 sm:p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <h2
          className="text-[22px] font-medium text-white tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {isFailed
            ? "Withdrawal Failed"
            : isComplete
              ? "Funds Withdrawn!"
              : "Withdrawal in Progress"}
        </h2>
      </div>

      {/* Amount */}
      {amountLabel && (
        <div className="mb-8">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-sr-pink-muted">
              Amount
            </span>
            <p
              className="text-[32px] font-medium leading-none tracking-tight text-white sm:text-[38px]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {amountLabel}
            </p>
          </div>

          <div className="mt-6 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 sm:gap-x-8">
            <span className="text-sm text-sr-pink-muted">From</span>
            <span className="text-sm font-medium text-sr-bubblegum">
              {brandName === "Sugar Rush" ? "Sugar Rush Trading Vault" : "Gummiworm Vault"}
            </span>

            <span className="text-sm text-sr-pink-muted">Status</span>
            <div className="flex items-center">
              <StatusBadge
                variant={statusDetail.variant}
                label={statusDetail.label}
              />
            </div>

            {settlementTxHash && (
              <>
                <span className="text-sm text-sr-pink-muted">Transaction</span>
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-white">
                    {settlementTxHash.slice(0, 8)}...{settlementTxHash.slice(-8)}
                  </span>
                  {explorerHref && (
                    <a
                      href={explorerHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sr-pink-muted hover:text-sr-pink-light transition-colors"
                    >
                      <ExternalLinkIcon />
                    </a>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Vertical stepper */}
      <div className="flex flex-col">
        {WITHDRAW_STAGES.map((s, i) => {
          const isDone = activeIdx > i;
          const isActive = activeIdx === i && !isFailed;
          const isFuture = !isDone && !isActive && i !== WITHDRAW_STAGES.length - 1;
          const variant: BadgeVariant = isDone
            ? "done"
            : isActive
              ? "active"
              : "waiting";

          return (
            <div key={i} className="flex items-center gap-4">
              {/* Icon + connector */}
              <div className="flex flex-col items-center self-stretch">
                {isDone ? (
                  <div className="w-8 h-8 rounded-full bg-sr-mint/70 flex items-center justify-center shrink-0">
                    <CheckIcon />
                  </div>
                ) : isActive ? (
                  <div className="w-8 h-8 rounded-full border-2 border-sr-tangerine border-t-transparent animate-spin shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full border border-sr-neutral-700/70 shrink-0" />
                )}
                {i < WITHDRAW_STAGES.length - 1 && (
                  <div
                    className={`w-px flex-1 min-h-[24px] ${
                      isDone ? "bg-sr-mint/15" : "bg-sr-neutral-700/20"
                    }`}
                  />
                )}
              </div>

              {/* Content */}
              <div className={`flex-1 flex items-center min-h-8 ${i < WITHDRAW_STAGES.length - 1 ? "pb-5" : ""}`}>
                <div className="flex w-full items-center justify-between">
                  <span
                    className={`text-sm font-medium ${
                      isActive
                        ? "text-white"
                        : isDone
                          ? "text-white/60"
                          : "text-sr-neutral-700/70"
                    }`}
                  >
                    {isActive ? s.activeLabel : s.label}
                  </span>
                  {i === WITHDRAW_STAGES.length - 1 && isComplete ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sr-mint/15 text-sr-mint">
                      Complete!
                    </span>
                  ) : (
                    <StatusBadge
                      variant={variant}
                      className={
                        isDone
                          ? "opacity-70"
                          : isFuture
                            ? "opacity-45"
                            : ""
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error */}
      {isFailed && error && (
        <div className="mt-6 flex flex-col gap-4">
          <div className="rounded-xl bg-sr-salmon/10 p-3">
            <p className="text-xs text-sr-salmon">{error}</p>
          </div>
          <button
            onClick={onRetry}
            className="w-full rounded-full border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-sm font-medium text-sr-neutral-300 transition-colors hover:bg-white/[0.05] hover:text-white"
          >
            Go back
          </button>
        </div>
      )}
    </div>
  );
};
